import { computed } from 'vue';
import { AppCoreEnums } from '../shared/enums';
import { IRouteMeta } from 'src/core/models/routing';
import { ROUTE_PATH_NEW, ROUTE_PATH_OVERVIEW, ROUTE_PATH_PROPERTIES } from '@/shared/constants';
import { RouteLocationNormalized, RouteRecordNormalized, RouteParams, useRoute, useRouter, RouteRecordRaw } from 'vue-router';

export default () => {
  const route = useRoute();
  const router = useRouter();

  const allPaginationContainerTypes = [
    AppCoreEnums.ContainerType.CollapsibleFiltersContainer,
    AppCoreEnums.ContainerType.CollapsibleTableContainer,
    AppCoreEnums.ContainerType.TableContainer,
    AppCoreEnums.ContainerType.TableInnerContainer,
    AppCoreEnums.ContainerType.LogsTableContainer,
    AppCoreEnums.ContainerType.ListContainer,
  ];

  const allDynamicFiltersContainerTypes = [
    AppCoreEnums.ContainerType.CollapsibleTableContainer,
    AppCoreEnums.ContainerType.TableContainer,
    AppCoreEnums.ContainerType.TableInnerContainer,
  ];

  const hasRouteContainer = (route: RouteRecordNormalized, containerType: AppCoreEnums.ContainerType) =>
    (<IRouteMeta>route.meta)?.containerType == containerType;

  const hasRouteInnerContainer = (route: RouteRecordNormalized, innerContainerType: AppCoreEnums.ContainerType) =>
    (<IRouteMeta>route.meta)?.innerContainerType == innerContainerType;

  const replaceRouteParamsWithValues = (routePath: string, routeParams: RouteParams) =>
    routePath
      ?.replace(':id', routeParams?.id?.toString() || ':id')
      ?.replace(':id2', routeParams?.id2?.toString() || ':id2')
      ?.replace(':id3', routeParams?.id3?.toString() || ':id3');

  const getResolvedRoutePaths = (route: RouteLocationNormalized) =>
    route.matched.map((x) => replaceRouteParamsWithValues(x.path, route.params));

  const getRouteWithContainer = (
    route: RouteLocationNormalized,
    containerType: AppCoreEnums.ContainerType
  ): RouteRecordNormalized | undefined => route.matched.find((x) => hasRouteContainer(x, containerType));

  const getRouteWithInnerContainer = (
    route: RouteLocationNormalized,
    containerType: AppCoreEnums.ContainerType
  ): RouteRecordNormalized | undefined => route.matched.find((x) => hasRouteInnerContainer(x, containerType));

  const getRouteIndexByParamName = (route: RouteLocationNormalized, paramName: string): number =>
    route.matched.findIndex((x) => x.path.includes(':' + paramName));

  const getRouteIndexByContainerType = (route: RouteLocationNormalized, containerType: AppCoreEnums.ContainerType): number =>
    route.matched.findIndex((x) => hasRouteContainer(x, containerType));

  const hasParentRouteContainer = (
    route: RouteLocationNormalized,
    containerType: AppCoreEnums.ContainerType,
    parentRouteIndex: number
  ): boolean => {
    if (parentRouteIndex < 0 || parentRouteIndex >= route.matched.length) return false;
    const parentRoute = route.matched[parentRouteIndex];
    return hasRouteContainer(parentRoute, containerType);
  };

  const getMostInnerTableBasedContainerType = (): AppCoreEnums.ContainerType | undefined => {
    for (var i = route.matched.length - 1; i >= 0; i--) {
      if (allPaginationContainerTypes.includes((route.meta as IRouteMeta)?.containerType))
        return (route.meta as IRouteMeta)?.containerType;
    }
    return undefined;
  };

  const getContainerRouteIndex = (route: RouteLocationNormalized, containerType: AppCoreEnums.ContainerType): number => {
    return route.matched.findIndex((x) => hasRouteContainer(x, containerType));
  };

  const getContainerInnerRouteIndex = (route: RouteLocationNormalized, containerType: AppCoreEnums.ContainerType): number => {
    return route.matched.findIndex((x) => hasRouteInnerContainer(x, containerType));
  };

  const getContainerRouteName = (route: RouteLocationNormalized, containerType: AppCoreEnums.ContainerType): string => {
    return route.matched.find((x) => (x.meta as IRouteMeta).containerType === containerType)?.name?.toString() || '';
  };

  const hasContainerChildRoutes = (
    route: RouteLocationNormalized,
    containerType: AppCoreEnums.ContainerType,
    canBeLeaf = false
  ): boolean => {
    const index = getContainerRouteIndex(route, containerType);
    return index != -1 && (canBeLeaf || index + 1 < route.matched.length);
  };

  const hasContainerChildInnerRoutes = (
    route: RouteLocationNormalized,
    containerType: AppCoreEnums.ContainerType,
    canBeLeaf = false
  ): boolean => {
    const index = getContainerInnerRouteIndex(route, containerType);
    return index != -1 && (canBeLeaf || index + 1 < route.matched.length);
  };

  const getContainerNewRouteName = (containerType: AppCoreEnums.ContainerType): string => {
    const containerRoute = route.matched.find((x) => (x.meta as IRouteMeta)?.containerType == containerType);
    if (containerRoute) {
      return containerRoute.children.find((x) => x.path === ROUTE_PATH_NEW)?.name?.toString() || '';
    }
    return '';
  };

  const getPropertiesRouteName = () => {
    if (route.matched.length < 2) return undefined;
    const moduleRootRouteName = route.matched[1].name?.toString();

    const activeModuleRoutes = router.getRoutes().find((x) => x.name == moduleRootRouteName);

    let foundRouteName: string | undefined = undefined;
    activeModuleRoutes?.children.forEach((x) => {
      foundRouteName = foundRouteName || searchPropertiesRoute(x);
    });

    return foundRouteName;
  };

  const searchPropertiesRoute = (routeRec: RouteRecordRaw) => {
    if (routeRec.path === ROUTE_PATH_PROPERTIES) return routeRec.name?.toString();
    if (routeRec.children?.length) {
      let foundRouteName: string | undefined = undefined;
      routeRec.children.forEach((x) => {
        foundRouteName = foundRouteName || searchPropertiesRoute(x)?.toString();
      });

      return foundRouteName;
    }
  };

  const isOverviewRoute = (): boolean => {
    return (
      (route.path.includes(ROUTE_PATH_OVERVIEW) &&
        !route?.matched.find(
          (x) => !!x.children.find((y) => (y.meta as IRouteMeta)?.containerType == AppCoreEnums.ContainerType.SideMenuContainer)
        )) ||
      !!route?.matched?.find((x) => x.path.includes(ROUTE_PATH_OVERVIEW))
    );
  };

  const getTabsContainerFirstRoute = (route: RouteLocationNormalized): string => {
    const collapsibleTableContainerRoute = route.matched.find(
      (x) => (x.meta as IRouteMeta).containerType === AppCoreEnums.ContainerType.CollapsibleTableContainer
    );
    const tabsContainerRoute = collapsibleTableContainerRoute?.children?.find(
      (x) => (x.meta as IRouteMeta).containerType === AppCoreEnums.ContainerType.TabsContainer
    );
    if (tabsContainerRoute?.children?.length) {
      return tabsContainerRoute.children[0].name?.toString() || '';
    }
    return '';
  };

  const getRouteWithParentContainer = (
    route: RouteLocationNormalized,
    containerType: AppCoreEnums.ContainerType
  ): RouteRecordNormalized | undefined => {
    return route.matched.find((x, index) => hasParentRouteContainer(route, containerType, index - 1));
  };

  const getFormContainerParentRoute = (): AppCoreEnums.ContainerType => {
    const formContainerIndex = getContainerRouteIndex(route, AppCoreEnums.ContainerType.FormsContainer);
    let foundFormParentContainer = AppCoreEnums.ContainerType.NotSpecifiedContainer;

    if (formContainerIndex) {
      for (
        let i = formContainerIndex - 1;
        i >= 0 && foundFormParentContainer === AppCoreEnums.ContainerType.NotSpecifiedContainer;
        i--
      ) {
        if ((route.matched[i].meta as IRouteMeta).containerType) return (route.matched[i].meta as IRouteMeta).containerType;
      }
    }
    return foundFormParentContainer;
  };

  const getContainerRouteMeta = (containerType: AppCoreEnums.ContainerType) =>
    computed(() => {
      const containerRoute = getRouteWithContainer(route, containerType) as RouteRecordNormalized;
      return containerRoute?.meta as IRouteMeta;
    });

  const getTitleField = (containerType: AppCoreEnums.ContainerType) =>
    computed(() => {
      let titleField = '' as string | undefined;
      let index = route.matched.findIndex((x) => hasRouteContainer(x, containerType));
      if (index > -1) {
        titleField = (route.matched[index].meta as IRouteMeta)?.collapsibleTableTitleField;
        if (!titleField) {
          //
          // search in parent routes.
          //
          index--;
          while (index >= 0 && !titleField) {
            titleField = (route.matched[index].meta as IRouteMeta)?.collapsibleTableTitleField;
            index--;
          }
        }
      }
      return titleField || '-';
    });

  const isFormInsideSideMenuContainer = computed(
    () => getFormContainerParentRoute() === AppCoreEnums.ContainerType.SideMenuContainer
  );

   const arePathFirstSegmentsDifferent = (path1: string, path2: string): boolean => {
    const segments1 = path1.replace(/^\/+/, "").split("/")
    const segments2 = path2.replace(/^\/+/, "").split("/")

    const firstSegment1 = segments1[0] || ""
    const firstSegment2 = segments2[0] || ""

    return firstSegment1 !== firstSegment2
  }

  return {
    getTitleField,
    isOverviewRoute,
    hasRouteContainer,
    getRouteWithContainer,
    getContainerRouteName,
    getResolvedRoutePaths,
    getContainerRouteMeta,
    getPropertiesRouteName,
    hasRouteInnerContainer,
    getContainerRouteIndex,
    hasContainerChildRoutes,
    getRouteIndexByParamName,
    getContainerNewRouteName,
    getRouteWithInnerContainer,
    getTabsContainerFirstRoute,
    getFormContainerParentRoute,
    allPaginationContainerTypes,
    getRouteWithParentContainer,
    hasContainerChildInnerRoutes,
    replaceRouteParamsWithValues,
    getRouteIndexByContainerType,
    arePathFirstSegmentsDifferent,
    isFormInsideSideMenuContainer,
    allDynamicFiltersContainerTypes,
    getMostInnerTableBasedContainerType,
  };
};
